import { Button } from '@components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@components/ui/dropdown-menu';
import { cn } from '@lib/utils';
import { STATUS_ERROR_MESSAGES } from '@utils/constants';
import { ArchiveIcon, ArchiveRestoreIcon, CheckIcon, ChevronDown, TrashIcon } from 'lucide-react';
import React, { useEffect, useState } from 'react';

type SelectedAction = 'archive' | 'delete' | 'unarchive';

type SoftDeleteButtonProps = {
  archived: boolean;
  archiveDescription?: string;
  canDelete?: boolean;
  className?: string;
  deleteDescription?: string;
  disabled?: boolean;
  isLoading?: boolean;
  itemType?: string;
  onArchive: () => void | Promise<void>;
  onDelete: () => void | Promise<void>;
  onUnarchive: () => void | Promise<void>;
  size?: 'sm' | 'default' | 'lg' | 'xs' | 'icon';
};

export const SoftDeleteButton: React.FC<SoftDeleteButtonProps> = (props) => {
  const {
    archived,
    archiveDescription: customArchiveDescription,
    canDelete = true,
    className,
    deleteDescription: customDeleteDescription,
    disabled = false,
    isLoading = false,
    itemType = 'item',
    onArchive,
    onDelete,
    onUnarchive,
    size = 'sm',
  } = props;

  const [selectedAction, setSelectedAction] = useState<SelectedAction>(archived ? 'unarchive' : 'archive');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Sync selectedAction with archived prop when it changes
  useEffect(() => {
    setSelectedAction(archived ? 'unarchive' : 'archive');
  }, [archived]);

  const archiveDescription = customArchiveDescription ?? 'Archive this item to hide it from regular views';
  const deleteDescription =
    customDeleteDescription ??
    (canDelete ? 'Permanently delete this item' : STATUS_ERROR_MESSAGES.CANNOT_DELETE_WITH_DEPENDENCIES(itemType));
  const unarchiveDescription = 'Restore this item to make it visible again';

  const handleMainButtonClick = () => {
    if (selectedAction === 'archive') {
      onArchive();
    } else if (selectedAction === 'unarchive') {
      onUnarchive();
    } else if (canDelete) {
      onDelete();
    }
  };

  const handleSelectAction = (action: SelectedAction) => {
    if (action === 'delete' && !canDelete) return;
    setSelectedAction(action);
    setIsDropdownOpen(false);
  };

  const getMainIcon = () => {
    if (selectedAction === 'archive') return ArchiveIcon;
    if (selectedAction === 'unarchive') return ArchiveRestoreIcon;
    return TrashIcon;
  };

  const getMainButtonLabel = () => {
    if (selectedAction === 'archive') return 'Archive';
    if (selectedAction === 'unarchive') return 'Unarchive';
    return 'Delete';
  };

  const MainIcon = getMainIcon();
  const mainButtonLabel = getMainButtonLabel();
  const mainButtonDisabled = disabled || (selectedAction === 'delete' && !canDelete);

  return (
    <DropdownMenu
      onOpenChange={setIsDropdownOpen}
      open={isDropdownOpen}
    >
      <div className={cn('inline-flex items-center', className)}>
        <Button
          className={cn(
            'rounded-r-none',
            selectedAction === 'delete' && 'border-red-500 bg-red-100 text-red-600 hover:bg-red-200',
          )}
          disabled={mainButtonDisabled}
          loading={isLoading}
          onClick={handleMainButtonClick}
          size={size}
          variant="secondary"
        >
          <MainIcon className="size-4 mr-2" />
          {mainButtonLabel}
        </Button>
        <DropdownMenuTrigger asChild>
          <Button
            aria-label="Open visibility actions"
            className={cn(
              'rounded-l-none border-l border-gray-200 px-2',
              selectedAction === 'delete' && 'border-red-500 bg-red-100 text-red-600 hover:bg-red-200 border-l-red-300',
            )}
            disabled={disabled}
            size={size}
            variant="secondary"
          >
            <ChevronDown className="size-4" />
          </Button>
        </DropdownMenuTrigger>
      </div>

      <DropdownMenuContent
        align="start"
        className="w-96"
      >
        {archived ? (
          <DropdownMenuItem
            className="flex items-start gap-3 py-3 cursor-pointer"
            onSelect={(event) => {
              event.preventDefault();
              handleSelectAction('unarchive');
            }}
          >
            <div className="flex items-center justify-center size-4 mt-0.5">
              {selectedAction === 'unarchive' ? (
                <CheckIcon className="size-4 text-primary" />
              ) : (
                <ArchiveRestoreIcon className="size-4" />
              )}
            </div>
            <div className="flex flex-col text-left">
              <span className="text-sm font-medium text-gray-900">Unarchive</span>
              <span className="text-xs text-muted-foreground">{unarchiveDescription}</span>
            </div>
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            className="flex items-start gap-3 py-3 cursor-pointer"
            onSelect={(event) => {
              event.preventDefault();
              handleSelectAction('archive');
            }}
          >
            <div className="flex items-center justify-center size-4 mt-0.5">
              {selectedAction === 'archive' ? (
                <CheckIcon className="size-4 text-primary" />
              ) : (
                <ArchiveIcon className="size-4" />
              )}
            </div>
            <div className="flex flex-col text-left">
              <span className="text-sm font-medium text-gray-900">Archive</span>
              <span className="text-xs text-muted-foreground">{archiveDescription}</span>
            </div>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          className={cn(
            'flex items-start gap-3 py-3 cursor-pointer',
            canDelete
              ? 'text-destructive hover:bg-red-50 hover:text-destructive focus:bg-red-50 focus:text-destructive'
              : 'opacity-50 cursor-not-allowed',
          )}
          disabled={!canDelete}
          onSelect={(event) => {
            event.preventDefault();
            handleSelectAction('delete');
          }}
        >
          <div className="flex items-center justify-center size-4 mt-0.5">
            {selectedAction === 'delete' ? (
              <CheckIcon className="size-4 text-destructive" />
            ) : (
              <TrashIcon className="size-4 text-destructive" />
            )}
          </div>
          <div className="flex flex-col text-left">
            <span className="text-sm font-medium text-destructive">Delete</span>
            <span className="text-xs text-muted-foreground">{deleteDescription}</span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
